import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ProductImage } from './index';
import './ImageZoomModal.css';

interface ImageZoomModalProps {
  images: ProductImage[];
  currentIndex: number;
  onClose: () => void;
  productTitle: string;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
  images,
  currentIndex,
  onClose,
  productTitle
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Touch handling states
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [lastTap, setLastTap] = useState<number>(0);
  const [swipeStartY, setSwipeStartY] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  const minVerticalSwipeDistance = 100;

  // Handle browser back button (desktop & mobile) to close zoom modal
  useEffect(() => {
    // Add a history state when modal opens
    const modalState = { modalOpen: true, type: 'imageZoom', timestamp: Date.now() };
    window.history.pushState(modalState, '', window.location.href);

    // Handle back button/gesture (works on both desktop and mobile)
    const handlePopState = (event: PopStateEvent) => {
      // Prevent the default navigation behavior
      event.preventDefault();

      // Close the modal instead of navigating away
      onClose();

      // Stop the event from propagating further
      event.stopImmediatePropagation();
    };

    // Add the listener with capture=true to ensure it catches the event first
    window.addEventListener('popstate', handlePopState, true);

    // Cleanup function
    return () => {
      window.removeEventListener('popstate', handlePopState, true);

      // Clean up history state if modal is still open
      // This prevents issues when component unmounts normally
      if (window.history.state?.modalOpen && window.history.state?.type === 'imageZoom') {
        try {
          window.history.back();
        } catch (error) {
          // Ignore cleanup errors - they're not critical
          console.debug('History cleanup completed');
        }
      }
    };
  }, [onClose]);

  // Prevent scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Memoize navigation functions to prevent unnecessary re-renders
  const nextImage = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const toggleZoom = useCallback(() => {
    setIsZoomed((prev) => {
      if (!prev) {
        setZoomPosition({ x: 50, y: 50 });
      }
      return !prev;
    });
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case ' ':
          e.preventDefault();
          toggleZoom();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, prevImage, nextImage, toggleZoom]);

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const currentTime = Date.now();
    setTouchStart({ x: touch.clientX, y: touch.clientY, time: currentTime });
    setTouchEnd(null);
    setSwipeStartY(touch.clientY);
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });

    // Update zoom position if zoomed
    if (isZoomed && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = ((touch.clientX - rect.left) / rect.width) * 100;
      const y = ((touch.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    }
  };

  // Handle touch end
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentTime = Date.now();
    const timeDiff = currentTime - touchStart.time;

    // If no touchEnd, it means no movement - treat as tap
    if (!touchEnd) {
      // Check if it's a quick tap (less than 300ms)
      if (timeDiff < 300) {
        const tapGap = currentTime - lastTap;

        if (tapGap < 500 && tapGap > 0) {
          // Double tap detected
          e.preventDefault();
          handleZoomToggle(e.changedTouches[0]);
        } else {
          // Single tap - for future use if needed
          setLastTap(currentTime);
        }
      }

      setTouchStart(null);
      setTouchEnd(null);
      setSwipeStartY(null);
      return;
    }

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    // If movement is minimal, treat as tap
    if (totalDistance < 10 && timeDiff < 300) {
      const tapGap = currentTime - lastTap;

      if (tapGap < 500 && tapGap > 0) {
        // Double tap detected
        e.preventDefault();
        handleZoomToggle(e.changedTouches[0]);
      } else {
        setLastTap(currentTime);
      }
    } else {
      // Handle swipes only if there was significant movement
      const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
      const isVerticalSwipe = Math.abs(distanceY) > Math.abs(distanceX);

      // Handle vertical swipe to close (only when not zoomed)
      if (!isZoomed && isVerticalSwipe && Math.abs(distanceY) > minVerticalSwipeDistance) {
        if (distanceY > 0) { // Swipe up
          onClose();
          return;
        }
      }

      // Handle horizontal swipe for navigation (only when not zoomed and multiple images)
      if (!isZoomed && images.length > 1 && isHorizontalSwipe && Math.abs(distanceX) > minSwipeDistance) {
        if (distanceX > 0) {
          nextImage(); // Swipe left to go to next
        } else {
          prevImage(); // Swipe right to go to previous
        }
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
    setSwipeStartY(null);
  };

  // Helper function to handle zoom toggle
  const handleZoomToggle = (touch: React.Touch) => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      // Calculate zoom position based on tap location
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 100;
        const y = ((touch.clientY - rect.top) / rect.height) * 100;
        setZoomPosition({
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y))
        });
      }
      setIsZoomed(true);
    }
  };

  // Handle mouse interactions (for desktop)
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    // Only handle mouse clicks on desktop (when touches are not available)
    if ('ontouchstart' in window) return;

    if (!isZoomed) {
      // Calculate zoom position based on click for zoom in
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x, y });
      setIsZoomed(true);
    } else {
      // Zoom out on second click
      setIsZoomed(false);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    // Only handle mouse move on desktop
    if ('ontouchstart' in window || !isZoomed) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle backdrop touch for mobile
  const handleBackdropTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && swipeStartY !== null) {
      const currentY = e.changedTouches[0].clientY;
      const swipeDistance = Math.abs(currentY - swipeStartY);

      // If it's a downward swipe on backdrop, close modal
      if (currentY > swipeStartY && swipeDistance > minVerticalSwipeDistance) {
        onClose();
      }
    }
  };

  const currentImage = images[activeIndex];

  return (
    <div
      className="image-zoom-modal-backdrop"
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropTouch}
    >
      <div className="image-zoom-modal-content" ref={containerRef}>
        {/* Header */}
        <div className="zoom-modal-header">
          <h3 className="zoom-modal-title">{productTitle}</h3>
          <div className="zoom-modal-controls">
            <button
              className="zoom-toggle-btn"
              onClick={toggleZoom}
              title={isZoomed ? 'Zoom Out' : 'Zoom In'}
            >
              {isZoomed ? <ZoomOutIcon /> : <ZoomInIcon />}
            </button>
            <button className="zoom-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {/* Main Image Container */}
        <div className="zoom-image-container">
          <img
            ref={imageRef}
            src={currentImage.url}
            alt={currentImage.alt}
            className={`zoom-image ${isZoomed ? 'zoomed' : ''}`}
            onClick={handleImageClick}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={
              isZoomed
                ? {
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    transform: 'scale(2.5)',
                    cursor: 'ontouchstart' in window ? 'default' : 'zoom-out'
                  }
                : {
                    cursor: 'ontouchstart' in window ? 'default' : 'zoom-in'
                  }
            }
          />

          {/* Navigation arrows (only show if multiple images) */}
          {images.length > 1 && (
            <>
              <button
                className="zoom-nav prev"
                onClick={prevImage}
                aria-label="Previous image"
              >
                ❮
              </button>
              <button
                className="zoom-nav next"
                onClick={nextImage}
                aria-label="Next image"
              >
                ❯
              </button>
            </>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div className="zoom-image-counter">
              {activeIndex + 1} / {images.length}
            </div>
          )}

          {/* Zoom hint - Updated for mobile */}
          {!isZoomed && (
            <div className="zoom-hint">
              {'ontouchstart' in window
                ? 'Double tap to zoom • Swipe to navigate • Swipe down to close'
                : 'Click to zoom • Use arrow keys to navigate • Press space to zoom'
              }
            </div>
          )}
        </div>

        {/* Thumbnail strip (only show if multiple images) */}
        {images.length > 1 && (
          <div className="zoom-thumbnail-strip">
            {images.map((image, index) => (
              <div
                key={image.id}
                className={`zoom-thumbnail ${index === activeIndex ? 'active' : ''}`}
                onClick={() => {
                  setActiveIndex(index);
                  setIsZoomed(false);
                }}
              >
                <img src={image.url} alt={image.alt} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Zoom In Icon
const ZoomInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>
  </svg>
);

// Zoom Out Icon
const ZoomOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    <path d="M7 9v1h5V9H7z"/>
  </svg>
);

export default ImageZoomModal;